﻿import React, { useState, useEffect } from 'react';
import { IonChromatographyBenchSheet } from '../models/ion-chromatography-bench-sheet';
import { AnalysisScopeType, BenchSheetTechnologyType, DocumentType, Entities, NoteType } from '../models/enums';
import { ItemBasicInformation } from '../models/item-basic-information';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import NoteViewer from './NoteViewer';
import BenchSheetToolbar from './BenchSheetToolbar';
import { DocumentationConstants } from '../store/documentation-constants';
import BenchSheetHeaderComponent from './BenchSheetHeaderComponent';
import BenchSheetData from './BenchSheetData';
import MinimumSampleSizeComponent from './MinimumSampleSizeComponent';
import SamplePreparationsComponent from './SamplePreparations/SamplePreparationsComponent';
import IcGradientsComponentData from './IcGradientsComponentData';
import IonChromatographyIonDataDataComponent from './IonChromatographyIonDataDataComponent tsx';
import IcColumnsComponent from './IcColumnsComponent';
import IcInstrument from './IcInstrument';
import { CrudFunctions } from '../utilities/crud-functions';
import IcDetectorComponent from './IcDetectorComponent';
import StandardsViewer from './StandardsViewer';
import { UserDataStore } from '../store/userDataStore';
import GlobalDialog from './Modal/GlobalDialog';
import { ModalMetadata } from '../models/modal-metadata';
import { ModalMessageType } from '../models/enums';
import TicketLogEntryForm from './TicketLogEntryForm';
import DmaFooter from './DmaFooter';

import { Button } from 'devextreme-react/button';
import { Popup as DialogPopup } from 'devextreme-react/popup';

let key = 1;

export default function IonChromatographyBenchSheetForm() {
    const [benchSheet, setBenchSheet] =
        useState<IonChromatographyBenchSheet>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [ticketLogEntryDialogVisible, setTicketLogEntryDialogVisible] =
        useState<boolean>();

    const [itemBasicInitInformation, setItemBasicInitInformation] =
        useState<ItemBasicInformation>();

    const [dataLoaded, setDataLoaded] =
        useState<boolean>();

    useEffect(() => {
        //setAuthorizations();
        if (!dataLoaded)
            getAllData();
    }, []);

    const parts = window.location.href.split('/');
    const benchSheetId = parts[parts.length - 1];

    key++;

    const user = UserDataStore.getCurrentUser();
    const canEditRoles = ["SA", "QCBS-A", "QCBS-L", "QCBS-T"];
    const canEdit = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canInsertRoles = ["SA", "QCBS-A", "QCBS-L"];
    const canInsert = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));
    const canDeleteRoles = ["SA", "QCBS-A"];
    const canDelete = canEditRoles.some(r => user.roleCodes && user.roleCodes.includes(r));

    return (
        <div className="pw-form-container">
            <div className="pw-content">

                <BenchSheetToolbar title="IC Bench Sheet"
                    subtitle="Ion Chromatography"
                    auditRowPointer={benchSheetId} entity={Entities.IcBenchSheets}
                    technologyId={BenchSheetTechnologyType.IonChromatography}
                    documentationId={DocumentationConstants.ICBenchSheet}
                    showItemInformation={showBenchSheetInformationDialog}
                    showTicketLog={showTicketLogEntryDialog} />

                <BenchSheetHeaderComponent benchSheetId={benchSheetId} readOnly={!canEdit} />

                <IcInstrument isReadOnly={!canEdit}
                    benchSheetId={benchSheetId} />

                <div className="pw-margin-bottom-05">
                    <IcDetectorComponent benchSheetId={benchSheetId} isReadOnly={!canEdit} />
                </div>

                <div className="pw-margin-bottom-05">
                    <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={true} />
                </div>

                <IcGradientsComponentData benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    datagridHeight="16rem" />

                <div>
                    <IcColumnsComponent benchSheetId={benchSheetId} isReadOnly={!canEdit} />
                </div>

                {(benchSheet && benchSheet.scopeTypes && benchSheet.scopeTypes.includes(AnalysisScopeType.Homogeneity)) &&
                    <MinimumSampleSizeComponent benchSheetId={benchSheetId} />
                }

                <SamplePreparationsComponent benchSheetId={benchSheetId} diluent={false} />

                <IonChromatographyIonDataDataComponent benchSheetId={benchSheetId} isReadOnly={!canEdit}
                    datagridHeight="16rem" />

                <div className="pw-comp-row-scroll-fixed-grid">
                    <BenchSheetData parentId={benchSheetId}
                        readOnly={!canEdit} canDelete={canDelete}
                        technology={BenchSheetTechnologyType.IonChromatography}
                        documentType={DocumentType.General}
                        imageHandlerTitle="C of A Images" />
                </div>

                {/*<div>*/}
                {/*    <StandardsViewer batchId={benchSheet && benchSheet.batchId ?*/}
                {/*        benchSheet.batchId : undefined}*/}
                {/*        technologyId={BenchSheetTechnologyType.IonChromatography} />*/}
                {/*</div>*/}

                <div className="pw-comp-row-scroll-fixed-grid">
                    <NoteViewer height='12rem' title="Bench Sheet Notes" readOnly={!canEdit}
                        noteType={NoteType.BenchSheet} parentId={benchSheetId} />
                </div>

                <DmaFooter />
            </div>

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width={'25rem'}
                    height={'21rem'}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInitInformation}
                        onOkClick={hideItemBasicInformationDialog}
                        key={`IBI${key}`} />
                </DialogPopup>
            }

            {ticketLogEntryDialogVisible &&
                <DialogPopup visible={ticketLogEntryDialogVisible}
                    onHiding={hideTicketLogEntryDialog}
                    title="Ticket Log Entry"
                    width={'55rem'}
                    height={'45rem'}
                    dragEnabled={true}
                    hideOnOutsideClick={false}
                    resizeEnabled={true}>
                    <TicketLogEntryForm formName="GC Bench Sheet" contextData={benchSheet}
                        onCancel={hideTicketLogEntryDialog}
                        onComplete={hideTicketLogEntryDialog}
                        key={`TLE${key}`} />
                </DialogPopup>
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    // get functions

    async function getBenchSheet() {
        await CrudFunctions.getData(`benchsheets/benchsheet/${benchSheetId}`)
            .then(data => {
                setBenchSheet(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheet");
            });
    }

    // show/hide

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(true);
    }

    function hideTicketLogEntryDialog() {
        setTicketLogEntryDialogVisible(false);
    }

    function showIssuesDialog() {
        //todo
    }

    function showBenchSheetInformationDialog() {
        if (benchSheet) {
            let itemBasicInitInformation: ItemBasicInformation = {
                rowPointer: benchSheetId,
                createTimestamp: benchSheet.createTimestamp,
                lastModifiedTimestamp: benchSheet.modifyTimestamp,
                createUser: benchSheet.createUser,
                modifyUser: benchSheet.modifyUser
            };

            setItemBasicInitInformation(itemBasicInitInformation);
            setItemBasicInformationDialogVisible(true);
        }
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }

    async function getAllData() {
        await Promise.all([
            getBenchSheet(),
        ])
            .then(() => {
                setDataLoaded(true);
            });
    }
}
